import { Injectable } from "@angular/core";
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { TranslateService } from "@ngx-translate/core";
import { FeatureActionsMap, IoConfigurationFeatureCodes } from "@omni/classes/authentication/user.class";
import { AuthenticationDataService } from "@omni/data-services/authentication/authentication.service";
import { startRecursiveEventScheduleConflictFlaggingForEventsArray } from "@omni/utility/activity.utility";
import { APP_VERSION } from "src/config/endpoints.config";
import { AccountOfflineService } from "./account/account.offline.service";
import { ActivityService } from "./activity/activity.service";
import { AuthenticationService } from "./authentication.service";
import { CaseManagementService } from "./case-management/case-management.service";
import { ContactOfflineService } from "./contact/contact.service";
import { DeviceService } from "./device/device.service";
import { EventsToolService } from "./events-tool/events-tool.service";
import { GlobalUtilityService } from "./global-utility.service";
import { TrackingEventNames, TrackService } from "./logging/tracking.service";
import { NavigationService, PageName } from "./navigation/navigation.service";
import { PharmacovigilanceService } from "./pharmacovigilance-reporting/pharmacovigilance.service";
import { PresentationService } from "./presentation/presentation.service";
import { ResourceService, ResourceView } from "./resource/resource.service";
import { AllocationFeatureService } from "./sample/allocation.feature.service";
import { TimeOffService } from "./time-off/time-off.service";
import { ComponentViewMode, PresentationView, UIService } from "./ui/ui.service";
import { electronApi } from "@omni/services/electron-api";
import * as CryptoJS from 'crypto-js';
import { ExternallinksDataService } from "@omni/data-services/externallink/externallinks.data.service";
import { LocalizationService } from "./localization/localization.service";
import { EmailService, MessageTemplateView } from "./email-templates/email.service";
import {
  ContactSpotlightComponent,
  ContactPageComponent,
  ExternalAppFrameComponent
} from "./lazy-components";
import { combineLatest, map, Observable, startWith } from "rxjs";
import { orderBy } from 'lodash-es';

@Injectable({
  providedIn: 'root'
})
export class GoAppNavigationService {

  private hasBaselineEnabled: boolean = false;
  public readonly menuOptions: Observable<any[]>;

  constructor(
    private readonly translate: TranslateService,
    private readonly utilityService: GlobalUtilityService,
    private readonly authenticationService: AuthenticationService,
    private readonly device: DeviceService,
    private readonly trackingService: TrackService,
    private readonly contactService: ContactOfflineService,
    private readonly uiService: UIService,
    private readonly accountService: AccountOfflineService,
    private readonly caseManagementService: CaseManagementService,
    private readonly navService: NavigationService,
    private readonly presentationService: PresentationService,
    private readonly pharmacovigilanceService: PharmacovigilanceService,
    private readonly eventsToolService: EventsToolService,
    private readonly inAppBrowser: InAppBrowser,
    private readonly authenticationDataService: AuthenticationDataService,
    private readonly resourceService: ResourceService,
    private readonly allocFeatureService: AllocationFeatureService,
    private readonly timeoffService: TimeOffService,
    private readonly activityService: ActivityService,
    private readonly externallinksDataService: ExternallinksDataService,
    private readonly localisationService: LocalizationService,
    private readonly emailService: EmailService,
  ) {

    this.menuOptions = combineLatest([
      this.authenticationService.user$,
      this.externallinksDataService.hyperlinks$.pipe(startWith([])),
      this.translate.onLangChange.pipe(startWith(null))
    ]).pipe(
      map(([_, hyperlinks]) => {
        this.hasBaselineEnabled = this.authenticationService.hasFeatureAction(FeatureActionsMap.BASELINE);
        if (!this.hasBaselineEnabled) {
          return [];
        }
        return [
          ...this.initDefaultMenuOptions(),
          ...this.mapHyperlinkList(hyperlinks)
        ];
      })
    );

  }

  private getDisplayNamesAsPerLang() {
    let textdisplayName;
    switch (this.utilityService.globalCustomerText) {
      case 'Stakeholder':
        textdisplayName = this.translate.instant('STAKEHOLDER');
        break;
      case 'Contact':
        textdisplayName = this.translate.instant('CONTACT');
        break;
      case 'Customer':
        textdisplayName = this.translate.instant('CUSTOMER');
        break;
      default:
        textdisplayName = this.utilityService.globalCustomerText;
        break;
    }
    let allocationOrderDisplayName = this.translate.instant('CUSTOMER_ALLOCATION_WITH_TEXT', { text: textdisplayName });
    switch (this.translate.currentLang) {
      case 'it':
      case 'pt':
      case 'es':
        allocationOrderDisplayName = this.translate.instant('CUSTOMER_ALLOCATION_TEXT_IN_MENU');
        break;
      default:
        allocationOrderDisplayName = this.translate.instant('CUSTOMER_ALLOCATION_WITH_TEXT', { text: textdisplayName });
        break;
    }
    return { textdisplayName, allocationOrderDisplayName };
  }

  private initDefaultMenuOptions() {
    this.hasBaselineEnabled = this.authenticationService.hasFeatureAction(FeatureActionsMap.BASELINE);
    if (this.hasBaselineEnabled) {
      let { textdisplayName, allocationOrderDisplayName } = this.getDisplayNamesAsPerLang();

      const options = [
        {
          id: 'accounts',
          visible: this.hasBaselineEnabled,
          iconPath: 'assets/imgs/nav/accounts.svg',
          displayName: this.translate.instant('ACCOUNTS'),
          selected: false,
          component: 'AccountsPage',
          enable: true
        },
        {
          id: 'accountManagement',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.ACCOUNT_MANAGEMENT),
          iconPath: 'assets/imgs/nav/account-plans.svg',
          displayName: this.translate.instant('ACCOUNT_PLANS'),
          selected: false,
          component: 'AccountManagementPage',
          enable: true
        },
        {
          id: 'marketingBusiness',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.MARKETING_BUSINESS_PLAN),
          iconPath: 'assets/imgs/nav/marketing-business-plans.svg',
          displayName: this.translate.instant('MARKETING_BUSINESS_PLANS'),
          selected: false,
          component: 'MarketingBusinessManagementPage',
          enable: true
        },
        {
          id: 'quotes',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.QUOTES),
          iconPath: 'assets/imgs/nav/quotes.svg',
          displayName: this.translate.instant('QUOTES'),
          selected: false,
          component: 'QuotePage',
          enable: true
        },
        {
          id: 'orderManagement',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.ORDER_MANAGEMENT)
            && !this.authenticationService.hasFeatureAction(FeatureActionsMap.NEW_ORDER_FLOW),
          iconPath: 'assets/imgs/nav/sales-orders.svg',
          displayName: this.translate.instant('SALES_ORDERS'),
          selected: false,
          component: 'OrderManagementPage',
          enable: true
        },
        // The new order flow's orders replace the legacy tool, as they do on the account timeline.
        {
          id: 'orderManagement',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.NEW_ORDER_FLOW),
          iconPath: 'assets/imgs/nav/sales-orders.svg',
          displayName: this.translate.instant('ORDER_MANAGEMENT'),
          selected: false,
          component: 'OrderManagementToolPage',
          enable: true
        },
        {
          id: 'call-plan',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.CALL_PLANS)
            && this.authenticationService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.ORG_CALLPLAN_GA_HIDE_CALL_PLAN_TOOL) !== true,
          iconPath: 'assets/imgs/nav/call-plans.svg',
          displayName: this.translate.instant('CALL_PLANS'),
          selected: false,
          component: 'CustomerCallPlanPage',
          enable: true
        },
        {
          id: 'case-tool',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.CASE_INTAKE),
          iconPath: 'assets/imgs/nav/customer-inquiries.svg',
          displayName: this.translate.instant('CUSTOMER_INQUIRIES_WITH_TEXT', { globalCustomerText: textdisplayName }),
          selected: false,
          component: 'CaseManagementHome',
          enable: true
        },
        {
          id: 'medical-inquiry',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.MEDICAL_INQUIRY_TOOL),
          iconPath: 'assets/imgs/nav/customer-inquiries.svg',
          displayName: this.translate.instant('MEDICAL_INQUIRY_TOOL') || 'Medical Inquiry',
          selected: false,
          component: 'MedicalInquiryHomePage',
          enable: true
        },
        {
          id: 'coaching',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.COACHING_TOOL),
          iconName: 'coaching-icon',
          iconPath: 'assets/imgs/nav/coaching.svg',
          displayName: this.translate.instant('COACHING'),
          selected: false,
          component: 'CoachingPage',
          enable: true
        },
        {
          id: 'contacts',
          visible: this.hasBaselineEnabled,
          iconPath: 'assets/imgs/nav/contacts.svg',
          displayName: this.translate.instant('CONTACTS'),
          selected: false,
          component: 'ContactPage',
          enable: true
        },
        {
          id: 'fieldMaterialManagement',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.FIELD_MATERIAL_MANAGEMENT),
          iconPath: 'assets/imgs/nav/field-asset-management.svg',
          displayName: this.translate.instant('FIELD_MATERIAL_MANAGEMENT'),
          selected: false,
          component: 'FieldMaterialManagementPage',
          enable: true
        },
        {
          id: 'presentations',
          visible: this.hasBaselineEnabled,
          iconPath: 'assets/imgs/nav/presentations.svg',
          displayName: this.translate.instant('PRESENTATIONS'),
          selected: false,
          component: 'PresentationPage',
          enable: true
        },
        {
          id: 'pharmacovigilance',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.PHARMACOVIGILANCE_TOOL),
          iconPath: 'assets/imgs/nav/adverse-events.svg',
          displayName: this.translate.instant('PHARMACOVIGILANCE_REPORTING'),
          selected: false,
          component: 'PharmacovigilanceComponent',
          enable: true
        },
        {
          id: 'voice-notes',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.VOICE_NOTE_TAKER),
          iconPath: 'assets/imgs/nav/voice-notes.svg',
          displayName: this.translate.instant('MY_NOTES'),
          selected: false,
          component: 'VoiceNotesPageComponent',
          enable: true
        },
        {
          id: 'analytics',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.OMNIPRESENCE_DNA),
          iconPath: 'assets/imgs/nav/analytics.svg',
          displayName: this.translate.instant('ANALYTICS'),
          selected: false,
          component: 'OmnipresenceExtURL',
          enable: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.OMNIPRESENCE_DNA) && !this.device.isOffline
        },
        {
          id: 'survey',
          visible: this.hasBaselineEnabled && (this.authenticationService.hasFeatureAction(FeatureActionsMap.CUSTOMER_SURVEY) || this.authenticationService.hasFeatureAction(FeatureActionsMap.ACCOUNT_SURVEY)),
          iconPath: 'assets/imgs/nav/survey.svg',
          displayName: this.translate.instant('SURVEY'),
          selected: false,
          component: 'SurveyPage',
          enable: true
        },
        {
          id: 'time-off',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.TIME_OFF_TOOL),
          iconName: 'time-off-icon',
          iconPath: 'assets/imgs/nav/time-off.svg',
          displayName: this.translate.instant('TIME_OFF'),
          selected: false,
          component: 'TimeoffPage',
          enable: true
        },
        {
          id: 'resources',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.RESOURCE_TOOL),
          iconPath: 'assets/imgs/nav/resources.svg',
          displayName: this.translate.instant('RESOURCES'),
          selected: false,
          component: 'ResourcesPage',
          enable: true
        },
        {
          id: 'allocation',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.ALLOCATION_TOOL),
          iconPath: 'assets/imgs/nav/customer-allocations.svg',
          displayName: allocationOrderDisplayName,
          selected: false,
          component: 'AllocationPage',
          enable: true,
        },
        {
          id: 'consent',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.CONSENT_TOOL)
            && this.authenticationService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.ORG_CONSENT_GA_HIDE_CONSENT_TOOL) !== true,
          iconPath: 'assets/imgs/nav/consent.svg',
          displayName: this.translate.instant('CONSENT'),
          selected: false,
          component: 'ConsentPage',
          enable: true
        },
        {
          id: 'scientificactivity',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.SCIENTIFIC_TOOL_PLAN),
          iconPath: 'assets/imgs/nav/scientific-plans.svg',
          displayName: this.translate.instant('SCIENTIFIC_PLANS'),
          selected: false,
          component: 'ScientificActivityPage',
          enable: true
        },
        {
          id: 'opportunitymgmt',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.OPPORTUNITY_MANAGEMENT),
          iconPath: 'assets/imgs/nav/opportunities.svg',
          displayName: this.translate.instant('OPPORTUNITIES'),
          selected: false,
          component: 'OpportunityManagementPage',
          enable: true
        },
        {
          id: 'marketscan',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.CUSTOMER_SCAN_TOOL),
          iconPath: 'assets/imgs/nav/customer-scan.svg',
          displayName: this.translate.instant('CUSTOMER_SCAN'),
          selected: false,
          component: 'MarketScanPage',
          enable: true
        },
        {
          id: 'scheduler',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.SCHEDULER),
          iconPath: 'assets/imgs/nav/scheduler.svg',
          displayName: this.translate.instant('SCHEDULER'),
          selected: false,
          component: 'SchedulerPage',
          enable: true
        },
        {
          id: 'eventstool',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.EVENT_TOOL),
          iconPath: 'assets/imgs/nav/events.svg',
          displayName: this.translate.instant('EVENTS'),
          selected: false,
          component: 'EventsToolPageComponent',
          enable: true
        },
        {
          id: 'message-templates',
          visible: this.hasBaselineEnabled && this.authenticationService.hasFeatureAction(FeatureActionsMap.MESSAGE_ACTIVITY),
          iconPath: 'assets/imgs/nav/message-templates.svg',
          displayName: this.translate.instant('MESSAGE_TEMPLATES'),
          selected: false,
          component: 'MessageTemplatesPage',
          enable: true
        },
      ];
      return orderBy(options.filter(op => op.visible), 'displayName');
    }
  }

  private mapHyperlinkList(hyperlinks: any[]): any[] {
    const mapped = (hyperlinks ?? [])
      .map(item => item && ({
        id: item.indskr_hyperlinkid,
        displayName: this.localisationService.getTranslatedString(item.indskr_lable ? item.indskr_lable : item.indskr_name),
        url: item.indskr_url,
        visible: true,
        selected: false,
        enable: true,
        iconName: 'tools_link',
        iconPath: item.iconUrl || 'assets/imgs/nav/external-link.svg',
        isBlob: item.iconUrl?.startsWith('blob:'),
        component: 'HyperlinkExtURL',
        openMode: item.indskr_openmode,
        hideHeader: !!item.indskr_hideheader,
      }))
      .filter(Boolean);
    return orderBy(mapped, 'displayName');
  }

  public async navigateToMenuOption(option) {
    const defaultParams = {
      __source: 'menu'
    };
    this.activityService.selectedActivity = undefined;
    this.activityService.activitySource = null;
    this.uiService.showRightPane = false;
    this.uiService.toolsActivityActive = true;
    this.uiService.isConsentFromToolDrawer = false;
    switch (option.component) {
      case "ContactPage":
        this.trackingService.tracking('ToolsContacts', TrackingEventNames.ACTIVITY);
        if (!this.contactService.loadedContacts && !this.contactService.approvableDCRRequests.length) return;
        this.uiService.showNewActivity = false;
        this.uiService.prevView = this.uiService.activeView
        this.uiService.activeView = "Contacts";
        this.uiService.consentFlipButton = false;
        this.contactService.contactInformation = null;
        this.contactService.prevSelected = null;
        this.accountService.accountPageMode = ComponentViewMode.LISTVIEW;
        this.contactService.contactPageMode = ComponentViewMode.LISTVIEW;
        this.contactService.accessedContactListFrom = PageName.ToolsDrawer;
        this.uiService.isContactsFromToolDrawer = true;
        this.contactService.consentFrom = 'ContactTool';
        if (this.navService.getActiveChildNavViewPageName() == PageName.CaseManagementDetailComponent) {
          this.caseManagementService.assignSelectedCase(undefined);
          this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, PageName.GoAppActivitiesPageComponent);
        }
        {
          const hasContactSpotlight = this.authenticationService.hasFeatureAction(FeatureActionsMap.CONTACT_SPOTLIGHT);
          const contactPage = hasContactSpotlight ? ContactSpotlightComponent : ContactPageComponent;
          let params: any = { from: 'ContactTool', ...defaultParams };
          if (hasContactSpotlight) {
            params.contactPageParams = {
              ...params
            };
          }
          this.navService.setRootWithTracking(contactPage, PageName.ContactPageComponent, params, PageName.ContactPageComponent);
        }
        break;
      case "ConsentPage":
        this.trackingService.tracking('ToolsConsent', TrackingEventNames.ACTIVITY);
        if (!this.contactService.loadedContacts) return;
        this.uiService.showNewActivity = false;
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "Consent";
        this.uiService.isConsentFromToolDrawer = true;
        this.uiService.consentFlipButton = false;
        this.contactService.contactInformation = null;
        this.contactService.prevSelected = null;
        this.accountService.accountPageMode = ComponentViewMode.LISTVIEW;
        this.contactService.contactPageMode = ComponentViewMode.LISTVIEW;
        this.contactService.accessedContactListFrom = PageName.ToolsDrawer;
        this.contactService.consentFrom = 'ConsentTool';
        if (this.navService.getActiveChildNavViewPageName() == PageName.CaseManagementDetailComponent) {
          this.caseManagementService.assignSelectedCase(undefined);
          this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, PageName.GoAppActivitiesPageComponent);
        }
        this.navService.setRootWithTracking(PageName.ContactPageComponent, PageName.ContactPageComponent, { from: 'ConsentTool', ...defaultParams }, PageName.ContactPageComponent);
        break;
      case "AccountsPage":
        this.trackingService.tracking('ToolsAccounts', TrackingEventNames.ACTIVITY)
        if (!this.accountService.loadedAccounts) return;
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "Accounts";
        this.accountService.accessedAccountListFrom = PageName.GoAppActivitiesPageComponent;
        this.accountService.accountPageMode = ComponentViewMode.LISTVIEW;
        this.contactService.contactPageMode = ComponentViewMode.LISTVIEW;
        this.uiService.isAccountsFromToolDrawer = true;
        this.navService.setRootWithTracking(PageName.AccountPageComponent, PageName.AccountPageComponent, { from: 'AccountTool', 'listMode': ComponentViewMode.LISTVIEW, ...defaultParams }, PageName.AccountPageComponent);
        break;
      case "AccountManagementPage":
        if (!this.accountService.loadedAccounts) return;
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "AccountManagement";
        this.uiService.activitiesPagePlanTabSelectedPlan = undefined;
        this.navService.setRootWithTracking(PageName.AccountManagementPageComponent, PageName.AccountManagementPageComponent, { 'listMode': ComponentViewMode.LISTVIEW, ...defaultParams }, PageName.AccountManagementPageComponent);
        break;
      case "MarketingBusinessManagementPage":
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "marketingBusiness";
        this.uiService.showNewActivity = false;
        this.navService.setRootWithTracking(PageName.MarketingPlanManagementPageComponent, PageName.MarketingPlanManagementPageComponent, { 'listMode': ComponentViewMode.LISTVIEW, ...defaultParams }, PageName.MarketingPlanManagementPageComponent);
        break;
      case "QuotePage":
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "quotes";
        this.uiService.showNewActivity = false;
        this.navService.setRootWithTracking(PageName.QuotePageComponent, PageName.QuotePageComponent, { 'listMode': ComponentViewMode.LISTVIEW, ...defaultParams }, PageName.QuotePageComponent);
        break;
      case "CustomerCallPlanPage":
        this.trackingService.tracking('ToolsCallPlans', TrackingEventNames.ACTIVITY)
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "CallPlans";
        this.navService.setRootWithTracking(PageName.CustomerCallPlanPage, PageName.CustomerCallPlanPage, { 'listMode': ComponentViewMode.LISTVIEW, ...defaultParams }, PageName.CustomerCallPlanPage);
        break;
      case "FieldMaterialManagementPage":
        this.uiService.prevView = this.uiService.activeView;
        this.navService.setRootWithTracking(PageName.FieldMaterialManagementPageComponent, PageName.FieldMaterialManagementPageComponent, defaultParams, PageName.FieldMaterialManagementPageComponent);
        break;
      case "PresentationPage":
        this.trackingService.tracking('ToolsPresentations', TrackingEventNames.ACTIVITY)
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "Presentation";
        this.presentationService.viewMode = PresentationView.MENU;
        this.navService.setRootWithTracking(PageName.PresentationPageComponent, PageName.PresentationPageComponent, defaultParams, PageName.PresentationPageComponent);
        //this.navCtrl.push(PresentationPageComponent);
        // this.viewCtrl.dismiss();
        break;
      case 'PharmacovigilanceComponent':
        this.pharmacovigilanceService.loadRecordsFromDB();
        this.pharmacovigilanceService.loadInfoButtonsDataFromDB(this.authenticationService.user.businessUnitName);
        this.uiService.prevView = this.uiService.activeView;
        this.navService.setRootWithTracking(
          PageName.PharmacovigilancePage,
          PageName.PharmacovigilancePage,
          defaultParams,
          PageName.PharmacovigilancePage,
        );
        break;
      case 'VoiceNotesPageComponent':
        this.uiService.prevView = this.uiService.activeView;
        this.navService.setRootWithTracking(
          PageName.VoiceNotesPageComponent,
          PageName.VoiceNotesPageComponent,
          defaultParams,
          PageName.VoiceNotesPageComponent,
        );
        break;
      case "OmnipresenceExtURL":
        this.uiService.toolsActivityActive = false;
        this.trackingService.tracking('ToolsAnalytics', TrackingEventNames.ACTIVITY)
        if (!this.device.isOffline) {
          const appInsightsInstrumentationKey = this.authenticationService.userConfig.activeInstance.appInsightsInstrumentationKey;
          let encJson = CryptoJS.AES.encrypt(JSON.stringify(appInsightsInstrumentationKey), 'analytics').toString()
          let encryptedAppInsightsKey = CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(encJson))

          let url = this.authenticationService.userConfig.activeInstance.dnaDashboardUrl +
            '?etoken=' + (await this.authenticationDataService.getEncryptedTokenForSelectedInstance()) +
            '&instanceid=' + this.authenticationService.userConfig.activeInstance.id +
            '&friendlyname=' + this.authenticationService.userConfig.activeInstance.friendlyName +
            '&appinsightskey=' + encryptedAppInsightsKey +
            '&appversion=' + APP_VERSION;

          if (this.device.isNativeApp && !this.device.deviceFlags.electron) {
            if (this.device.deviceFlags.ios) {
              const browserRef = this.inAppBrowser.create(url, '_blank', { location: 'no', hideurlbar: 'yes', zoom: 'no' });
            }
            else {
              const browserRef = this.inAppBrowser.create(url, '_system', { location: 'yes', toolbar: 'yes', zoom: 'no' });
            }
          } else {
            if (this.device.deviceFlags.electron) {
              electronApi.openExternal(url);
            } else {
              window.open(url, "_system");
            }
          }
        }
        break;
      case "ResourcesPage":
        this.trackingService.tracking('ToolsResources', TrackingEventNames.ACTIVITY)
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "Resources";
        this.resourceService.viewMode = ResourceView.MENU;
        this.navService.setRootWithTracking(PageName.ResourcePageComponent, PageName.ResourcePageComponent, defaultParams, PageName.ResourcePageComponent);
        break;
      case "TimeoffPage":
        this.trackingService.tracking('ToolsTimeOff', TrackingEventNames.ACTIVITY)
        this.uiService.prevView = this.uiService.activeView;
        this.timeoffService.setMobileTracker("timeOff");
        this.navService.setRootWithTracking(PageName.TimeOffComponent, PageName.TimeOffComponent, defaultParams, PageName.TimeOffComponent);
        //this.navCtrl.push(TimeOffComponent);
        break;
      case "AllocationPage":
        this.allocFeatureService.loadShipmentsForAllocationTool();
        this.allocFeatureService.loadTeamAdjustments();
        this.trackingService.tracking('ToolsCustomerAllocations', TrackingEventNames.ACTIVITY)
        this.activityService.indexWholeSampleActivityMetasBySKU();
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "CustomerAllocation";
        this.navService.setRootWithTracking(PageName.AllocationComponent, PageName.AllocationComponent, defaultParams, PageName.AllocationComponent);
        break;
      case "CoachingPage":
        this.trackingService.tracking('ToolsCoaching', TrackingEventNames.ACTIVITY)
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "Coaching";
        this.navService.setRootWithTracking(PageName.CoachingComponent, PageName.CoachingComponent, defaultParams, PageName.CoachingComponent);
        break;
      case "ScientificActivityPage":
        this.trackingService.tracking('ScientificPlansToolsDrawer', TrackingEventNames.SCIENTIFICACTIVITYPLAN, true);
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "ScientificActivity";
        this.navService.setRootWithTracking(PageName.ScientificActivityPage, PageName.ScientificActivityPage, defaultParams, PageName.ScientificActivityPage);
        break;
      case "CaseManagementHome":
        this.trackingService.tracking('ToolsCustomerInquiries', TrackingEventNames.ACTIVITY);
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "Case-Mangement";
        this.caseManagementService.assignSelectedCase(undefined);
        this.caseManagementService.showNewCase = false;
        this.navService.setRootWithTracking(PageName.CaseManagementHomePage, PageName.CaseManagementHomePage, defaultParams, PageName.CaseManagementHomePage);
        break;
      case "MedicalInquiryHomePage":
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "MedicalInquiry";
        this.navService.setRootWithTracking(PageName.MedicalInquiryHomePage, PageName.MedicalInquiryHomePage, defaultParams, PageName.MedicalInquiryHomePage);
        break;
      case "OrderManagementPage":
        this.trackingService.tracking('OrderManagementToolsDrawer', TrackingEventNames.ORDERMANAGEMENT);
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "OrderManagement";
        this.navService.setRootWithTracking(PageName.OrderManagementPageComponent, PageName.OrderManagementPageComponent, defaultParams, PageName.OrderManagementPageComponent);
        break;
      case "OrderManagementToolPage":
        this.trackingService.tracking('OrderManagementToolsDrawer', TrackingEventNames.ORDERMANAGEMENT);
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = "OrderManagement";
        this.navService.setRootWithTracking(PageName.OrderManagementToolPage, PageName.OrderManagementToolPage, defaultParams, PageName.OrderManagementToolPage);
        break;
      case "OpportunityManagementPage":
        if (!this.accountService.loadedAccounts) return;
        this.uiService.prevView = this.uiService.activeView;
        this.navService.setRootWithTracking(PageName.OpportunityManagementPage, PageName.OpportunityManagementPage, { 'listMode': ComponentViewMode.LISTVIEW, ...defaultParams }, PageName.OpportunityManagementPage);
        break;
      case "MarketScanPage":
        this.uiService.prevView = this.uiService.activeView;
        this.trackingService.tracking('CustomerScanToolClick', TrackingEventNames.CUSTOMER_SCAN, true);
        this.navService.setRootWithTracking(PageName.MarketScanPageComponent, PageName.MarketScanPageComponent, defaultParams, PageName.MarketScanPageComponent);
        break;
      case "EventsToolPageComponent":
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = '';
        await this.uiService.displayLoader();
        this.eventsToolService.clearConflictEventIds();
        const lastIdx = startRecursiveEventScheduleConflictFlaggingForEventsArray(this.eventsToolService.eventsToolData, 0, this.eventsToolService.conflictCheckLastIdx, this.authenticationService.user.xSystemUserID);
        this.eventsToolService.setConflictCheckLastIdx(lastIdx);
        this.navService.setRootWithTracking(PageName.EventsToolPageComponent, PageName.EventsToolPageComponent, defaultParams, PageName.EventsToolPageComponent);
        await this.uiService.dismissLoader();
        break;
      case "SurveyPage":
        this.uiService.prevView = this.uiService.activeView;
        this.trackingService.tracking('SurveyPageToolsDrawer', TrackingEventNames.SURVEY, true);
        this.navService.setRootWithTracking(PageName.SurveyPageComponent, PageName.SurveyPageComponent, defaultParams, PageName.SurveyPageComponent);
        break;
      case "SchedulerPage":
        this.uiService.prevView = this.uiService.activeView;
        this.navService.setRootWithTracking(PageName.SchedulerPageComponent, PageName.SchedulerPageComponent, defaultParams, PageName.SchedulerPageComponent);
        break;
      case "MessageTemplatesPage":
        this.trackingService.tracking('ToolsMessageTemplates', TrackingEventNames.MESSAGE_TEMPLATES);
        this.uiService.prevView = this.uiService.activeView;
        this.uiService.activeView = 'MessageTemplates';
        this.emailService.messageTemplateViewMode = MessageTemplateView.MENU;
        this.navService.setRootWithTracking(PageName.EmailTemplatePageComponent, PageName.EmailTemplatePageComponent, defaultParams, PageName.EmailTemplatePageComponent);
        break;
      case "HyperlinkExtURL": {
        this.uiService.toolsActivityActive = false;
        let url = option.url;
        if (option.openMode === 1) {
          this.navService.setRootWithTracking(ExternalAppFrameComponent, PageName.ExternalAppFrameComponent, { 'listMode': ComponentViewMode.LISTVIEW, url, title: option.displayName, hideHeader: option.hideHeader }, PageName.ExternalAppFrameComponent);
          break;
        } else if (option.openMode === 2) {
          this.inAppBrowser.create(url, '_blank', { location: 'no', hideurlbar: 'yes', zoom: 'no' });
          break;
        }
        if (this.device.isNativeApp && !this.device.deviceFlags.electron) {
          if (this.device.deviceFlags.ios) {
            this.inAppBrowser.create(url, '_blank', { location: 'no', hideurlbar: 'yes', zoom: 'no' });
          } else {
            this.inAppBrowser.create(url, '_system', { location: 'yes', toolbar: 'yes', zoom: 'no' });
          }
        } else {
          if (this.device.deviceFlags.electron) {
            electronApi.openExternal(url);
          } else {
            window.open(url, "_system");
          }
        }
      }
        break;
    }
  }
}
